import { useEffect, useCallback } from 'react';
import { create } from 'zustand';
import {
  clientAuthorizedGet,
  clientAuthorizedPost,
  clientAuthorizedPatch,
  clientAuthorizedDelete,
} from 'utils/fetchUtils';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { API_ROUTES } from 'utils/constants';
import {
  cacheCategories,
  cacheVoidedCategories,
  getCachedCategories,
  getCachedVoidedCategories,
} from 'utils/categoriesIndexedDBUtils';
import { syncPendingCategories } from 'utils/categorySyncService';
import Category from 'types/category';

let categoryChannelRef: BroadcastChannel | null = null;
let isRemoteCategoryUpdate = false;
let fetchCategoriesRef: ((isReload?: boolean) => Promise<void>) | null = null;
/** Tracks which company we've completed initial load for. Skip re-fetch if already loaded. */
let initializedCompanyId: string | null = null;
/** Avoid re-clearing the store when multiple components call useCategoryInit. */
let lastCompanyResetId: string | null | undefined;

interface CategoryStore {
  categories: { [key: string]: Category };
  voidedCategories: { [key: string]: Category };
  loading: boolean;
  reloading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  updateCategories: (categories: { [key: string]: Category }) => void;
  setVoidedCategories: (categories: { [key: string]: Category }) => void;
  addVoidedCategory: (category: Category) => void;
  addCategory: (newCategory: Category) => void;
  restoreVoidedCategory: (category: Category) => void;
  removeCategory: (categoryId: string) => void;
  setCategories: (categories: { [key: string]: Category }) => void;
  reset: () => void;
}

export const useCategoryStore = create<CategoryStore>((set) => ({
  categories: {},
  voidedCategories: {},
  loading: true,
  reloading: false,
  error: null,

  reload: async () => {
    if (fetchCategoriesRef) {
      await fetchCategoriesRef(true);
    }
  },

  updateCategories: (updatedCategories) => {
    set({ categories: updatedCategories });
    void cacheCategories(updatedCategories);
    if (!isRemoteCategoryUpdate) {
      categoryChannelRef?.postMessage(updatedCategories);
    }
    isRemoteCategoryUpdate = false;
  },

  setVoidedCategories: (voidedCategories) => set({ voidedCategories }),

  addVoidedCategory: (category) => {
    set((state) => {
      const voidedCategories = {
        ...state.voidedCategories,
        [category.id]: category,
      };
      void cacheVoidedCategories(voidedCategories);
      return { voidedCategories };
    });
  },

  addCategory: (newCategory) => {
    set((state) => {
      const updated = { ...state.categories, [newCategory.id]: newCategory };
      void cacheCategories(updated);
      categoryChannelRef?.postMessage(updated);
      return { categories: updated };
    });
  },

  restoreVoidedCategory: (category) => {
    set((state) => {
      const updatedVoided = { ...state.voidedCategories };
      delete updatedVoided[category.id];
      const updatedActive = { ...state.categories, [category.id]: category };
      void cacheCategories(updatedActive);
      void cacheVoidedCategories(updatedVoided);
      categoryChannelRef?.postMessage(updatedActive);
      return {
        categories: updatedActive,
        voidedCategories: updatedVoided,
      };
    });
  },

  removeCategory: (categoryId) => {
    set((state) => {
      const updated = { ...state.categories };
      delete updated[categoryId];
      void cacheCategories(updated);
      categoryChannelRef?.postMessage(updated);
      return { categories: updated };
    });
  },

  setCategories: (categories) => set({ categories }),

  reset: () =>
    set({ categories: {}, voidedCategories: {}, loading: true, error: null }),
}));

export function useCategoryInit(): void {
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const setCategories = useCategoryStore((s) => s.setCategories);

  const fetchCategories = useCallback(
    async (isReload = false) => {
      const companyId = useActiveCompanyStore.getState().activeCompanyId;
      if (!isCompanyInitialized || !companyId) return;

      // Idempotent: skip initial fetch if already loaded for this company
      if (!isReload && initializedCompanyId === companyId) return;

      useCategoryStore.setState(
        isReload ? { reloading: true } : { loading: true },
      );
      useCategoryStore.setState({ error: null });

      try {
        const fetchedCategories = await clientAuthorizedGet<{
          [key: string]: Category;
        }>(API_ROUTES.CATEGORY, undefined, companyId);

        initializedCompanyId = companyId;
        useCategoryStore.setState({ categories: fetchedCategories });
        await cacheCategories(fetchedCategories);
        categoryChannelRef?.postMessage(fetchedCategories);

        let fetchedVoidedCategories: { [key: string]: Category } = {};
        try {
          fetchedVoidedCategories = await clientAuthorizedGet<{
            [key: string]: Category;
          }>(API_ROUTES.CATEGORY_VOIDED, undefined, companyId);
          await cacheVoidedCategories(fetchedVoidedCategories);
        } catch (voidedErr) {
          console.warn('Failed to load voided categories', voidedErr);
        }

        useCategoryStore.setState({
          voidedCategories: fetchedVoidedCategories,
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load categories. Please check your connection.';
        useCategoryStore.setState({ error: errorMessage });
      } finally {
        useCategoryStore.setState(
          isReload ? { reloading: false } : { loading: false },
        );
      }
    },
    [isCompanyInitialized],
  );

  fetchCategoriesRef = fetchCategories;

  useEffect(() => {
    const channel = new BroadcastChannel('categories');
    categoryChannelRef = channel;

    channel.onmessage = (event: MessageEvent<{ [key: string]: Category }>) => {
      isRemoteCategoryUpdate = true;
      setCategories(event.data);
      void cacheCategories(event.data);
    };

    return () => {
      channel.close();
      categoryChannelRef = null;
    };
  }, [setCategories]);

  useEffect(() => {
    if (lastCompanyResetId === activeCompanyId) {
      return;
    }
    lastCompanyResetId = activeCompanyId ?? null;
    initializedCompanyId = null;
    useCategoryStore.setState(
      activeCompanyId
        ? { loading: true, categories: {}, voidedCategories: {}, error: null }
        : { loading: false, categories: {}, voidedCategories: {}, error: null },
    );
  }, [activeCompanyId]);

  useEffect(() => {
    const loadCache = async () => {
      if (!isCompanyInitialized || !activeCompanyId) return;
      try {
        const [cached, cachedVoided] = await Promise.all([
          getCachedCategories(),
          getCachedVoidedCategories(),
        ]);
        const hasCached = Object.keys(cached).length > 0;
        const hasCachedVoided = Object.keys(cachedVoided).length > 0;
        if (hasCached || hasCachedVoided) {
          useCategoryStore.setState({
            ...(hasCached ? { categories: cached, loading: false } : {}),
            ...(hasCachedVoided ? { voidedCategories: cachedVoided } : {}),
          });
        }
      } catch (err) {
        console.warn('Failed to load cached categories', err);
      }
    };

    void loadCache();
    // Intentionally not depending on activeCompanyId: IndexedDB cache is not
    // company-scoped; refetch on company change is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [isCompanyInitialized]);

  useEffect(() => {
    if (!isCompanyInitialized) return;

    if (!activeCompanyId) {
      useCategoryStore.setState({
        loading: false,
        categories: {},
        voidedCategories: {},
        error: null,
      });
      return;
    }

    void fetchCategories();
  }, [isCompanyInitialized, activeCompanyId, fetchCategories]);

  const syncPending = useCallback(async () => {
    if (!navigator.onLine || !isCompanyInitialized) return;

    const companyId = useActiveCompanyStore.getState().activeCompanyId;
    if (!companyId) return;

    try {
      const result = await syncPendingCategories({
        post: (url, body) =>
          clientAuthorizedPost(url, body as Record<string, unknown>, companyId),
        patch: (url, body) =>
          clientAuthorizedPatch(
            url,
            body as Record<string, unknown>,
            companyId,
          ),
        delete: (url) => clientAuthorizedDelete(url, undefined, companyId),
      });

      if (result.synced > 0 && fetchCategoriesRef) {
        await fetchCategoriesRef(true);
      }
    } catch (err) {
      console.warn('Failed to sync pending categories', err);
    }
  }, [isCompanyInitialized]);

  useEffect(() => {
    void syncPending();
    const handleOnline = () => {
      void syncPending();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncPending]);
}
